import React, { useEffect, useRef, useState } from "react";
import {
  ArrowRight, Check, ChevronRight, FileText, Target, ScanSearch, Layers3, Linkedin,
  Briefcase, KanbanSquare, Globe, FileCheck2, PenLine, Share2,
} from "lucide-react";
import { AiIcon } from "@/components/ui/ai-icon";

// Original landing palette (green ink, gold accent, cream surfaces).
export function landingTone(isLight) {
  return {
    heading: isLight ? "text-[#3D9970]" : "text-white",
    muted: isLight ? "text-[#6B8578]" : "text-[#9FB8AC]",
    card: isLight ? "border-[#DCEBE3] bg-white" : "border-white/10 bg-[#16332A]",
    border: isLight ? "border-[#DCEBE3]" : "border-white/10",
    alt: isLight ? "border-black/5 bg-[#FAFAF9]" : "border-white/10 bg-[#1C3B30]",
    tint: isLight ? "bg-[#FFEBD7] text-[#A67C52]" : "bg-[#A67C52]/15 text-[#D4A574]",
    btnPrimary:
      "inline-flex items-center justify-center gap-2 rounded-full bg-[#A67C52] px-6 py-3 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(166,124,82,0.55)] transition-colors hover:bg-[#8A6640] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] focus-visible:ring-offset-2",
    btnSecondary: `inline-flex items-center justify-center gap-2 rounded-full border px-6 py-3 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] focus-visible:ring-offset-2 ${
      isLight ? "border-black/15 text-[#3D9970] hover:border-black/40" : "border-white/20 text-white hover:border-[#A67C52]"
    }`,
  };
}

export function SectionHeading({ eyebrow, title, copy, isLight, align = "center" }) {
  const t = landingTone(isLight);
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-xl"}>
      {eyebrow && <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-[#A67C52]">{eyebrow}</p>}
      <h2 className={`font-['Manrope'] text-3xl font-bold leading-tight tracking-tight md:text-[2.75rem] ${t.heading}`}>{title}</h2>
      {copy && <p className={`mt-4 text-base leading-7 md:text-lg ${align === "center" ? "mx-auto max-w-xl" : ""} ${t.muted}`}>{copy}</p>}
    </div>
  );
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// A short recorded loop of the real product (scripts/screens/clips.py).
// Plays muted only while on screen; reduced-motion visitors get the still.
function ProductClip({ clip, alt }) {
  const ref = useRef(null);
  const [still] = useState(prefersReducedMotion);
  useEffect(() => {
    const v = ref.current;
    if (!v || still || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) v.play().catch(() => {});
      else v.pause();
    }, { threshold: 0.35 });
    io.observe(v);
    return () => io.disconnect();
  }, [still]);
  if (still) return <img src={`${clip}.webp`} alt={alt} loading="lazy" decoding="async" className="block aspect-[8/5] w-full" />;
  return (
    <video
      ref={ref}
      data-testid="landing-product-clip"
      className="block aspect-[8/5] w-full"
      src={`${clip}.mp4`}
      poster={`${clip}.webp`}
      muted
      loop
      playsInline
      preload="none"
      aria-label={alt}
    />
  );
}

function BrowserShot({ src, clip, alt, isLight, className = "" }) {
  const t = landingTone(isLight);
  return (
    <div className={`overflow-hidden rounded-xl border shadow-[0_24px_60px_-30px_rgba(15,33,27,0.45)] ${t.card} ${className}`}>
      <div className={`flex items-center gap-1.5 border-b px-3 py-2 ${t.border}`}>
        <span className="size-2 rounded-full bg-[#FF5F57]" /><span className="size-2 rounded-full bg-[#FEBC2E]" /><span className="size-2 rounded-full bg-[#28C840]" />
      </div>
      {clip ? <ProductClip clip={clip} alt={alt} /> : <img src={src} alt={alt} loading="lazy" decoding="async" className="block w-full" />}
    </div>
  );
}

// Featured services: the three things people come for, each with a short
// recorded loop of the real product (fictional "Alex Rivera" sample data).
// `clip` is the base path: .mp4 (loop), .webp (poster/still), .gif (share).
const FEATURED = [
  {
    testid: "copilot", icon: AiIcon, eyebrow: "Build", title: "AI Career Copilot",
    copy: "Chat your way to a complete resume. The Copilot drafts, rewrites and tailors every section from your real experience — it doesn't invent it.",
    points: ["Start from scratch, a PDF or LinkedIn", "Rewrites bullets with measurable impact", "Works directly on your live resume"],
    cta: "Build with AI", route: "/editor", navState: { startNew: true, initialAiMode: "chat" },
    clip: "/landing/clips/copilot", alt: "Improve with AI rewriting a work experience entry, checked against the resume, then applied in one click",
  },
  {
    testid: "optimizer", icon: ScanSearch, eyebrow: "Optimize", title: "ATS Resume Optimizer",
    copy: "See how applicant tracking systems read your resume, then fix what matters with grounded, one-click suggestions.",
    points: ["ATS score with keyword & skills coverage", "Fixes highlighted right on your resume", "Grammar and formatting checks"],
    cta: "Check my resume", route: "/ats-optimizer",
    clip: "/landing/clips/ats", alt: "Running the ATS check, then applying a grounded fix that rewrites a bullet on the resume",
  },
  {
    testid: "style", icon: Layers3, eyebrow: "Design", title: "Smart Style Studio",
    copy: "26 designer templates and adaptive Smart Layouts. Change fonts, colors and spacing — the layout re-balances as your content grows.",
    points: ["26 templates + Smart Layouts", "AI design recommendations", "One-page fit and PDF export"],
    cta: "Style my resume", route: "/style-studio",
    clip: "/landing/clips/studio", alt: "AI Style Studio switching between resume designs while the same content restyles live",
  },
];

// Everything else included in the platform -- every card opens its real tool.
const MORE_SERVICES = [
  { icon: Target, title: "Job Match", copy: "Fit score and evidence for one specific job.", route: "/job-match-analyzer" },
  { icon: PenLine, title: "Tailor Resume", copy: "Compile a version targeted to a role.", route: "/resume-compiler" },
  { icon: FileText, title: "Cover Letters", copy: "Job-specific letters from the same evidence.", route: "/cover-letter-generator" },
  { icon: Linkedin, title: "LinkedIn Optimizer", copy: "Headline and About that match your resume.", route: "/ai-studio/linkedin" },
  { icon: Briefcase, title: "Job Search", copy: "Roles that fit your resume.", route: "/jobs", badge: "Premium" },
  { icon: KanbanSquare, title: "Application Tracker", copy: "Every application, one board.", route: "/tracker" },
  { icon: Globe, title: "Digital Portfolio", copy: "A shareable page for your career.", route: "/digital-portfolio" },
  { icon: FileCheck2, title: "ATS Resume Checker", copy: "Free instant check, no sign-up.", route: "/ats-resume-checker", badge: "Free" },
];

export function ServicesSection({ isLight, navigate }) {
  const t = landingTone(isLight);
  return (
    <section id="products-section" className="scroll-mt-20 px-5 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          isLight={isLight}
          eyebrow="Everything in one workspace"
          title="Every tool you need to land the job."
          copy="Build, optimize, design and apply — one connected career profile powers all of it, so you never re-type your history."
        />

        <div className="mt-16 space-y-20 md:space-y-28">
          {FEATURED.map((f, i) => {
            const Icon = f.icon;
            return (
              <div key={f.testid} className="grid items-center gap-10 lg:grid-cols-12 lg:gap-14">
                <div className={`lg:col-span-5 ${i % 2 ? "lg:order-2" : ""}`}>
                  <div className="flex items-center gap-3">
                    <span className={`grid size-10 place-items-center rounded-xl ${t.tint}`}><Icon className="size-5" /></span>
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#A67C52]">{f.eyebrow}</span>
                  </div>
                  <h3 className={`mt-5 font-['Manrope'] text-2xl font-bold tracking-tight md:text-4xl ${t.heading}`}>{f.title}</h3>
                  <p className={`mt-4 text-base leading-7 ${t.muted}`}>{f.copy}</p>
                  <ul className="mt-6 space-y-2.5">
                    {f.points.map((p) => (
                      <li key={p} className={`flex items-start gap-2.5 text-sm font-medium ${isLight ? "text-[#1F3A2F]" : "text-[#E3EFE9]"}`}>
                        <span className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-[#3D9970]/15 text-[#3D9970]"><Check className="size-3" /></span>{p}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    data-testid={`differentiator-card-${f.testid}`}
                    onClick={() => navigate(f.route, f.navState ? { state: f.navState } : undefined)}
                    className={`mt-8 ${t.btnPrimary}`}
                  >
                    {f.cta} <ArrowRight className="size-4" />
                  </button>
                  <a
                    href={`${f.clip}.gif`}
                    download={`resumepilot360-${f.testid}.gif`}
                    data-testid={`landing-clip-share-${f.testid}`}
                    className={`mt-4 flex w-fit items-center gap-1.5 text-xs font-semibold underline-offset-4 hover:underline ${t.muted}`}
                  >
                    <Share2 className="size-3.5" /> Share this clip (GIF)
                  </a>
                </div>
                <button
                  type="button"
                  aria-label={`Open ${f.title}`}
                  onClick={() => navigate(f.route, f.navState ? { state: f.navState } : undefined)}
                  className={`group relative lg:col-span-7 ${i % 2 ? "lg:order-1" : ""} rounded-xl text-left transition-transform duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] focus-visible:ring-offset-4`}
                >
                  <div aria-hidden="true" className={`absolute -inset-4 -z-10 rounded-[2rem] blur-2xl ${isLight ? "bg-[#FFEBD7]/70" : "bg-[#A67C52]/10"}`} />
                  <BrowserShot clip={f.clip} alt={f.alt} isLight={isLight} />
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-24">
          <p className={`text-center text-sm font-bold uppercase tracking-[0.18em] ${t.muted}`}>Also included</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {MORE_SERVICES.map(({ icon: Icon, title, copy, route, badge }) => (
              <button
                key={title}
                type="button"
                onClick={() => navigate(route)}
                className={`group flex items-start gap-3 rounded-xl border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 hover:border-[#A67C52]/50 hover:shadow-[0_16px_32px_-20px_rgba(166,124,82,0.45)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A67C52] ${t.card}`}
              >
                <span className={`grid size-9 shrink-0 place-items-center rounded-lg ${t.tint}`}><Icon className="size-4" /></span>
                <span className="min-w-0">
                  <span className={`flex items-center gap-2 text-sm font-bold ${t.heading}`}>
                    {title}
                    {badge && <span className="rounded-full border border-[#A67C52]/40 px-1.5 text-[9px] font-bold uppercase tracking-wider text-[#A67C52]">{badge}</span>}
                  </span>
                  <span className={`mt-0.5 block text-xs leading-5 ${t.muted}`}>{copy}</span>
                </span>
                <ChevronRight className={`ml-auto mt-2 size-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 ${t.muted}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// Real Smart Layout renders of the same sample resume (see LandingPage's
// RESUME_VARIANTS) -- a scrollable gallery instead of a rotating carousel.
export function LayoutsSection({ isLight, navigate, variants }) {
  const t = landingTone(isLight);
  return (
    <section id="templates-section" className={`scroll-mt-20 border-y py-20 md:py-24 ${t.alt}`}>
      <div className="mx-auto max-w-6xl px-5">
        <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading
            isLight={isLight}
            align="left"
            eyebrow="Templates & Smart Layouts"
            title="One resume. Eight looks. Zero reformatting."
            copy="Same content, rendered by Smart Layout in different designs. Pick one — your content flows into it automatically."
          />
          <button type="button" data-testid="landing-explore-all-templates" onClick={() => navigate("/templates")} className={`shrink-0 ${t.btnSecondary}`}>
            Explore Layouts <ArrowRight className="size-4" />
          </button>
        </div>
      </div>
      <div className="mt-12 overflow-x-auto pb-4 [scrollbar-width:thin]">
        <ul className="mx-auto flex w-max gap-5 px-5 lg:px-[max(1.25rem,calc((100vw-72rem)/2+1.25rem))]">
          {variants.map((v) => (
            <li key={v.id} className="w-56 shrink-0 sm:w-64">
              <button
                type="button"
                onClick={() => navigate(`/r/${v.code}`)}
                aria-label={`View the ${v.label} Smart Layout resume`}
                className="group block w-full text-left focus-visible:outline-none"
              >
                <div className={`overflow-hidden rounded-lg border shadow-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-[0_20px_40px_-20px_rgba(166,124,82,0.5)] group-focus-visible:ring-2 group-focus-visible:ring-[#A67C52] ${t.border}`}>
                  <img src={v.image} alt={`${v.label} Smart Layout resume design`} loading="lazy" decoding="async" className="block aspect-[8.5/11] w-full bg-white object-cover object-top" />
                </div>
                <p className={`mt-3 text-sm font-bold ${t.heading}`}>{v.label}</p>
                <p className={`text-xs leading-5 ${t.muted}`}>{v.description}</p>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function FinalCtaSection({ onStartBuilding, navigate }) {
  return (
    <section className="px-5 pb-24 pt-8">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-[#3D9970] px-6 py-16 text-center text-white md:px-12 md:py-20">
        <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-24 size-80 rounded-full bg-[#A67C52]/40 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-20 size-80 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <h2 className="font-['Manrope'] text-3xl font-bold tracking-tight md:text-5xl">Build your next application in minutes.</h2>
          <p className="mx-auto mt-5 max-w-xl leading-7 text-white/75">Start free. Turn your career history into a focused resume and cover letter for the opportunity ahead.</p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <button type="button" data-testid="final-cta-build-resume" onClick={() => onStartBuilding()} className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-[#3D9970] transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3D9970]">
              Build My Resume <ArrowRight className="size-4" />
            </button>
            <button type="button" data-testid="landing-run-ats-check-btn" onClick={() => navigate("/ats-optimizer")} className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white">
              Check My ATS Score
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
